import type { AuthProvider } from '@refinedev/core';
import { apiClient } from '@src/utils/axios';
import { ROUTES } from '@src/routes/constants';
import { getDefaultRouteForUser } from '@src/utils/userAccess';

export const authProvider: AuthProvider = {
  login: async ({ username, email, password }) => {
    try {
      if (email || username) {
        const response = await apiClient.post('/auth/login', {
          email,
          password,
        });

        if (!response.data.error) {
          const userType =
            response.data.data?.user?.type || response.data.user?.type;
          return {
            success: true,
            redirectTo: getDefaultRouteForUser(userType),
          };
        }
      }

      return {
        success: false,
        error: {
          name: 'LoginError',
          message: 'Invalid credentials',
        },
      };
    } catch (error) {
      return {
        success: false,
        error: {
          name: 'LoginError',
          message: 'Login failed. Please try again.',
        },
      };
    }
  },

  logout: async () => {
    try {
      // Call logout endpoint to clear server-side session and HttpOnly cookies
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Logout error handled silently - continue with client-side cleanup
    }

    // Clear any non-HttpOnly cookies that might exist
    clearAuthCookies();

    // Clear all cookies from all possible paths and domains
    clearAllCookies();

    // Clear browser history to prevent going back after logout
    if (typeof window !== 'undefined') {
      // Replace current history entry to prevent going back
      window.history.replaceState(null, '', '/login');
      // Clear all history entries
      window.history.pushState(null, '', '/login');
      window.history.pushState(null, '', '/login');
      window.history.back();
      window.history.back();
    }

    return {
      success: true,
      redirectTo: '/login',
    };
  },

  check: async () => {
    try {
      // Since we're using HttpOnly cookies, we can't check document.cookie
      // Instead, we'll make an API call to verify authentication

      const response = await apiClient.get('auth/profile');

      if (response.data && !response.data.error && response.data.data?.user) {
        return { authenticated: true };
      } else {
        return {
          authenticated: false,
          error: new Error('Not authenticated'),
          logout: true,
          redirectTo: '/login',
        };
      }
    } catch (error) {
      return {
        authenticated: false,
        error: new Error('Authentication check failed'),
        logout: true,
        redirectTo: '/login',
      };
    }
  },

  getPermissions: async () => {
    // For demo purposes, return basic permissions
    return ['read', 'write'];
  },

  getIdentity: async () => {
    try {
      // Check if user is authenticated by calling the whoami endpoint
      const response = await apiClient.get('auth/profile');

      if (response.data && !response.data.error && response.data.data?.user) {
        // Handle nested response structure: { error: false, data: { user: {...} } }
        return response.data.data.user;
      } else if (response.data && !response.data.error && response.data.user) {
        // Handle direct user in response: { error: false, user: {...} }
        return response.data.user;
      }
    } catch (error) {
      // Authentication check failed silently
      console.error('Failed to get user identity:', error);
    }

    return null;
  },

  onError: async error => {
    // Handle 401 errors globally
    if (error?.status === 401) {
      // Clear cookies and redirect
      clearAuthCookies();
      clearAllCookies();

      return {
        error: {
          message: 'Please log in to continue',
          name: 'Unauthorized',
        },
        logout: true,
        redirectTo: '/login',
      };
    }

    return { error };
  },
};

// Improved cookie utility functions
function getCookie(name: string): string | undefined {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    const cookieValue = parts.pop()?.split(';').shift();
    if (cookieValue) {
      try {
        const decodedValue = decodeURIComponent(cookieValue);
        return decodedValue;
      } catch (error) {
        return cookieValue;
      }
    }
  }
  return undefined;
}

function checkForAuthCookies(): boolean {
  // Check for common authentication cookie names
  const authCookieNames = ['token'];

  for (const cookieName of authCookieNames) {
    const cookieValue = getCookie(cookieName);
    if (cookieValue && cookieValue.trim() !== '') {
      return true;
    }
  }

  // Also check if there are any cookies at all (some backends use generic names)
  const allCookies = document.cookie;
  if (allCookies && allCookies.trim() !== '') {
    // Parse all cookies to see what we have
    const cookies = allCookies.split(';').map(cookie => cookie.trim());

    // If we have any cookies, let's assume one of them is for auth
    // This is a fallback for custom cookie names
    return cookies.length > 0;
  }

  return false;
}

function clearAuthCookies(): void {
  const authCookieNames = ['token'];

  authCookieNames.forEach(cookieName => {
    document.cookie = `${cookieName}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  });
}

function clearAllCookies(): void {
  // Get all cookies and clear them
  const cookies = document.cookie.split(';');

  cookies.forEach(cookie => {
    const eqPos = cookie.indexOf('=');
    const name = eqPos > -1 ? cookie.substr(0, eqPos).trim() : cookie.trim();

    // Clear cookie with various path and domain combinations
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=${window.location.hostname}`;
    document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/; domain=.${window.location.hostname}`;
  });
}
